import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Package, FolderTree, ShoppingCart, FileText, ListChecks, Users, CreditCard,
  Truck, Settings, History, LogOut, Menu, ExternalLink,
} from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { http, fileUrl } from "@/api";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/admin", label: "Resumen", icon: LayoutDashboard, end: true },
  { to: "/admin/pedidos", label: "Pedidos", icon: ShoppingCart },
  { to: "/admin/cotizaciones", label: "Cotizaciones", icon: FileText },
  { to: "/admin/formulario", label: "Formulario de cotización", icon: ListChecks },
  { to: "/admin/productos", label: "Productos", icon: Package },
  { to: "/admin/categorias", label: "Categorías", icon: FolderTree },
  { to: "/admin/clientes", label: "Clientes", icon: Users },
  { to: "/admin/pagos", label: "Pagos", icon: CreditCard },
  { to: "/admin/envios", label: "Envíos y entregas", icon: Truck },
  { to: "/admin/configuracion", label: "Configuración", icon: Settings },
  { to: "/admin/historial", label: "Historial", icon: History },
];

function NavItems({ onClick }) {
  return (
    <nav className="flex flex-col gap-1">
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onClick}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${isActive ? "bg-[var(--brand-primary)] text-white" : "text-neutral-700 hover:bg-neutral-100"}`}
          data-testid={`admin-nav-${n.to.split("/")[2] || "resumen"}`}>
          <n.icon size={17} /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { adminToken, setToken, settings, reloadSettings } = useStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!adminToken) { navigate("/admin/login"); return; }
    http.get("/auth/me").then(() => reloadSettings()).catch(() => { setToken(""); navigate("/admin/login"); });
  }, [adminToken, navigate, setToken]); // eslint-disable-line

  if (!adminToken) return null;
  const logout = () => { setToken(""); navigate("/admin/login"); };

  const Brand = (
    <div className="flex items-center gap-3 px-2 mb-6">
      {settings?.logo_url && <img src={fileUrl(settings.logo_url)} alt="" className="h-10 w-10 object-contain" />}
      <div>
        <div className="font-serif text-lg font-bold leading-none">{settings?.business_name || "Floral Rossé"}</div>
        <div className="text-[11px] text-neutral-500 mt-1">Panel administrativo</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F6F3EE]" data-testid="admin-layout">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-white border-r border-black/5 p-4 overflow-y-auto">
        {Brand}
        <NavItems />
        <div className="mt-auto pt-6 space-y-1">
          <Link to="/" target="_blank" className="flex items-center gap-3 px-3 py-2 text-sm text-neutral-600 hover:text-[var(--brand-primary)]"><ExternalLink size={16} /> Ver tienda</Link>
          <button onClick={logout} className="flex items-center gap-3 px-3 py-2 text-sm text-neutral-600 hover:text-[var(--brand-primary)] w-full" data-testid="admin-logout"><LogOut size={16} /> Cerrar sesión</button>
        </div>
      </aside>
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-black/5 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {settings?.logo_url && <img src={fileUrl(settings.logo_url)} alt="" className="h-8 w-8" />}
          <span className="font-serif font-bold">Panel admin</span>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="rounded-full" data-testid="admin-mobile-menu"><Menu size={18} /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 overflow-y-auto">
            <SheetTitle className="sr-only">Menú</SheetTitle>
            <div className="pt-4">{Brand}<NavItems onClick={() => setOpen(false)} /></div>
            <div className="mt-6 space-y-1">
              <Link to="/" target="_blank" className="flex items-center gap-3 px-3 py-2 text-sm"><ExternalLink size={16} /> Ver tienda</Link>
              <button onClick={logout} className="flex items-center gap-3 px-3 py-2 text-sm w-full"><LogOut size={16} /> Cerrar sesión</button>
            </div>
          </SheetContent>
        </Sheet>
      </header>
      <main className="lg:pl-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
